/**
 * CABActionBar — Bottom action bar for the CAB left panel.
 *
 * States:
 * - idle: "Generate Blueprint" + "Delete All" buttons
 * - processing: "Generating..." indicator + "Stop" button
 * - paused: "Paused" indicator + Stop + Resume buttons
 * - completed: "Blueprint complete" + "Delete All" button
 * - cancelled: "Delete All" + "Generate Blueprint" buttons
 * - error: Error message + Resume + "Delete All" buttons
 */

import React, { useState } from 'react';
import { Text, Icon, Modal } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { CABSessionStatus } from '../../types/cab';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';
import { PlatformType } from '../../types/project';
import { getPlatformConfig } from '../../config/platformConfig';

interface CABActionBarProps {
  status: CABSessionStatus;
  canGenerate: boolean;
  hasFiles: boolean;
  onGenerate: () => void;
  onPause: () => Promise<void> | void;
  onStop: () => Promise<void> | void;
  onDeleteAll: () => void;
  onResume?: () => void;
  isResuming?: boolean;
  isUploading?: boolean;
  permissions?: string[];
  platformType?: PlatformType;
  onGenerateDesign?: () => Promise<void>;
}

export const CABActionBar: React.FC<CABActionBarProps> = ({
  status,
  canGenerate,
  hasFiles,
  onGenerate,
  onPause,
  onStop,
  onDeleteAll,
  onResume,
  isResuming = false,
  isUploading = false,
  permissions = [],
  platformType,
  onGenerateDesign,
}) => {
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isGeneratingDesign, setIsGeneratingDesign] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Determine visibility of Stop & Delete All
  const showStop = status === 'processing';
  const showDeleteAll = hasFiles && status !== 'processing' && status !== 'paused' && status !== 'error';
  const showBar = showStop || showDeleteAll || status === 'idle' || status === 'paused' || status === 'completed' || status === 'error' || status === 'cancelled';

  if (!showBar) return null;

  return (
    <>
      <div className="px-bg-v2-xl py-bg-v2-lg border-t border-border-secondary flex justify-between items-center gap-bg-v2-sm">
        {/* Status indicator (left side) */}
        <div className="flex-1 flex items-center gap-bg-v2-sm min-w-0">
          {status === 'error' && (
            <>
              <Icon name={IconName.Alert} size="sm" className="!text-text-error-primary" inheritColor />
              <Text variant="text-xs" weight="medium" className="text-text-error-primary">
                Generation failed
              </Text>
            </>
          )}
          {status === 'paused' && (
            <>
              <Icon name={IconName.Pause} size="sm" className="!text-text-warning-primary" inheritColor />
              <Text variant="text-xs" weight="medium" className="text-text-warning-primary">
                Generation paused
              </Text>
            </>
          )}
        </div>

        {/* Action buttons (right side) */}
        <div className="flex items-center gap-bg-v2-sm shrink-0">
          {/* Pause & Cancel buttons — only during processing */}
          {showStop && (
            <IfHasPermission
              permissions={permissions}
              requires={Permissions.UpdateProjectPermission}
            >
              <button
                type="button"
                disabled={isPausing || isStopping}
                onClick={async () => {
                  try {
                    setIsPausing(true);
                    await onPause();
                  } finally {
                    setIsPausing(false);
                  }
                }}
                className="
                  px-bg-v2-lg py-bg-v2-md border border-border-primary rounded-bg-v2-md
                  bg-bg-primary text-text-secondary text-bg-v2-sm font-bg-v2-medium
                  flex items-center gap-bg-v2-xs transition-colors
                  hover:bg-bg-primary-hover
                  disabled:opacity-40 disabled:cursor-not-allowed
                "
              >
                {isPausing ? (
                  <Icon name={IconName.Loading} size="sm" spin inheritColor />
                ) : (
                  <Icon name={IconName.Pause} size="sm" inheritColor />
                )}
                {isPausing ? 'Pausing...' : 'Pause'}
              </button>
              <button
                type="button"
                disabled={isPausing || isStopping}
                onClick={() => setShowStopConfirm(true)}
                className="
                  px-bg-v2-lg py-bg-v2-md border border-border-error rounded-bg-v2-md
                  bg-bg-primary text-text-error-primary text-bg-v2-sm font-bg-v2-medium
                  flex items-center gap-bg-v2-xs transition-colors
                  hover:bg-bg-error-primary
                  disabled:opacity-40 disabled:cursor-not-allowed
                "
              >
                {isStopping ? (
                  <Icon name={IconName.Loading} size="sm" spin inheritColor />
                ) : (
                  <Icon name={IconName.Delete} size="sm" inheritColor />
                )}
                {isStopping ? 'Stopping...' : 'Stop'}
              </button>
            </IfHasPermission>
          )}

          {/* Delete All button — when files exist and not processing */}
          {showDeleteAll && (
            <IfHasPermission
              permissions={permissions}
              requires={Permissions.UpdateProjectPermission}
            >
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="
                  px-bg-v2-lg py-bg-v2-md border border-border-error rounded-bg-v2-md
                  bg-bg-primary text-text-error-primary text-bg-v2-sm font-bg-v2-medium
                  flex items-center gap-bg-v2-xs transition-colors
                  hover:bg-bg-error-primary
                "
              >
                <Icon name={IconName.Delete} size="sm" inheritColor />
                {isUploading ? 'Stop & Delete All' : 'Delete All'}
              </button>
            </IfHasPermission>
          )}

          {/* Generate button — idle or cancelled state */}
          {(status === 'idle' || status === 'cancelled') && (
            <IfHasPermission
            permissions={permissions}
            requires={Permissions.UpdateProjectPermission}
          >
            <button
              type="button"
              onClick={onGenerate}
              disabled={!canGenerate}
              className="
                bg-bg-brand-solid text-text-primary-on-brand
                px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md
                font-bg-v2-semibold text-bg-v2-sm
                transition-colors container-shadow-sm
                hover:bg-bg-brand-solid-hover
                disabled:opacity-40 disabled:cursor-not-allowed
              "
            >
              Generate Blueprint
            </button>
            </IfHasPermission>
          )}

          {/* Stop button — paused or error state */}
          {(status === 'paused' || status === 'error') && (
            <IfHasPermission
              permissions={permissions}
              requires={Permissions.UpdateProjectPermission}
            >
              <button
                type="button"
                disabled={isStopping}
                onClick={() => setShowStopConfirm(true)}
                className="
                  px-bg-v2-lg py-bg-v2-md border border-border-error rounded-bg-v2-md
                  bg-bg-primary text-text-error-primary text-bg-v2-sm font-bg-v2-medium
                  flex items-center gap-bg-v2-xs transition-colors
                  hover:bg-bg-error-primary
                  disabled:opacity-40 disabled:cursor-not-allowed
                "
              >
                {isStopping ? (
                  <Icon name={IconName.Loading} size="sm" spin inheritColor />
                ) : (
                  <Icon name={IconName.Delete} size="sm" inheritColor />
                )}
                {isStopping ? 'Stopping...' : 'Stop'}
              </button>
            </IfHasPermission>
          )}

          {/* Resume button — error or paused state */}
          {(status === 'error' || status === 'paused') && onResume && (
            <IfHasPermission
              permissions={permissions}
              requires={Permissions.UpdateProjectPermission}
            >
              <button
                type="button"
                onClick={onResume}
                disabled={isResuming}
                className="
                  bg-bg-brand-solid text-text-primary-on-brand
                  px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md
                  font-bg-v2-semibold text-bg-v2-sm
                  transition-colors container-shadow-sm
                  hover:bg-bg-brand-solid-hover
                  disabled:opacity-40 disabled:cursor-not-allowed
                "
              >
                Resume
              </button>
            </IfHasPermission>
          )}

          {/* Generate Design button — completed state for Low Code projects */}
          {status === 'completed' && platformType && onGenerateDesign && (
            <IfHasPermission
              permissions={permissions}
              requires={Permissions.UpdateProjectPermission}
            >
              <button
                type="button"
                disabled={isGeneratingDesign}
                onClick={async () => {
                  try {
                    setIsGeneratingDesign(true);
                    await onGenerateDesign();
                  } catch (err) {
                    console.error('Failed to generate design:', err);
                  } finally {
                    setIsGeneratingDesign(false);
                  }
                }}
                className="
                  bg-bg-brand-solid text-text-primary-on-brand
                  px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md
                  font-bg-v2-semibold text-bg-v2-sm
                  transition-colors container-shadow-sm
                  hover:bg-bg-brand-solid-hover
                  disabled:opacity-40 disabled:cursor-not-allowed
                "
              >
                {isGeneratingDesign
                  ? 'Loading...'
                  : getPlatformConfig(platformType).generateButtonLabel}
              </button>
            </IfHasPermission>
          )}
        </div>
      </div>

      {/* Stop confirmation modal */}
      <Modal
        isOpen={showStopConfirm}
        onClose={() => { if (!isStopping) setShowStopConfirm(false); }}
        title="Stop Generation"
        size="sm"
        onSubmit={async () => {
          try {
            setIsStopping(true);
            setShowStopConfirm(false);
            await onStop();
          } finally {
            setIsStopping(false);
          }
        }}
        onCancel={() => { if (!isStopping) setShowStopConfirm(false); }}
        submitLabel="Stop"
        cancelLabel="Cancel"
      >
        <Text color="secondary">
          Are you sure you want to stop the blueprint generation? This will cancel the current progress and delete all generated artifacts. Use "Pause" instead if you want to resume later.
        </Text>
      </Modal>

      {/* Delete All confirmation modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => { if (!isDeleting) setShowDeleteConfirm(false); }}
        title={isUploading ? "Stop & Delete All" : "Delete All"}
        size="sm"
        onSubmit={async () => {
          try {
            setIsDeleting(true);
            await Promise.resolve(onDeleteAll());
          } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
          }
        }}
        onCancel={() => { if (!isDeleting) setShowDeleteConfirm(false); }}
        submitLabel={isUploading ? "Stop & Delete All" : "Delete All"}
        cancelLabel="Cancel"
        submitVariant="error"
        submitLoading={isDeleting}
      >
        <Text color="secondary">
          {isUploading
            ? 'Are you sure you want to stop all uploads and delete all files and the generated blueprint? This action cannot be undone.'
            : 'Are you sure you want to delete all uploaded files and the generated blueprint? This action cannot be undone.'}
        </Text>
      </Modal>
    </>
  );
};
